import { useState } from "react";
import {
  useAiSources,
  useCreateAiSource,
  useUpdateAiSource,
  useDeleteAiSource,
  useTriggerAiRun,
  useAiWorkerStatus,
} from "@/hooks/useAiAgent";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/ui/action-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Play, Pencil, Trash2, AlertTriangle, Settings2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { TableCard } from "@/components/ui/table-card";
import type { AiSource } from "@/hooks/useAiAgent";
import { AiSourceRulesDialog } from "@/components/ai/AiSourceRulesDialog";

export default function AiSourcesPage() {
  const { data: sources, isLoading } = useAiSources();
  const createSource = useCreateAiSource();
  const updateSource = useUpdateAiSource();
  const deleteSource = useDeleteAiSource();
  const triggerRun = useTriggerAiRun();
  const worker = useAiWorkerStatus();
  const canRunWebsites = worker.data?.available === true && !worker.isError;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [rulesSource, setRulesSource] = useState<AiSource | null>(null);
  const [editingSource, setEditingSource] = useState<AiSource | null>(null);
  const [form, setForm] = useState({
    name: "",
    url: "",
    type: "website" as "website" | "json_api",
    instructions: "",
  });

  const openCreate = () => {
    setEditingSource(null);
    setForm({ name: "", url: "", type: "website", instructions: "" });
    setDialogOpen(true);
  };

  const openEdit = (source: AiSource) => {
    setEditingSource(source);
    setForm({
      name: source.name,
      url: source.url,
      type: source.type,
      instructions: source.instructions,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingSource) {
        await updateSource.mutateAsync({
          id: editingSource.id,
          name: form.name,
          url: form.url,
          type: form.type,
          instructions: form.instructions,
        });
        toast.success("Źródło zaktualizowane");
      } else {
        await createSource.mutateAsync({
          name: form.name,
          url: form.url,
          type: form.type,
          instructions: form.instructions,
        });
        toast.success("Źródło dodane");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd zapisu");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSource.mutateAsync(id);
      toast.success("Źródło usunięte");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd usuwania");
    }
  };

  const handleRunAll = async () => {
    try {
      await triggerRun.mutateAsync(undefined);
      toast.success("Wyszukiwanie stron WWW dodane do kolejki");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd uruchamiania");
    }
  };

  const handleRunSource = async (sourceId: string) => {
    try {
      await triggerRun.mutateAsync(sourceId);
      toast.success("Agent uruchomiony dla źródła");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd uruchamiania");
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="sr-only">Agent AI — Źródła wydarzeń</h1>
      <div className="flex items-start justify-between gap-4">
        <p className="text-pulso-muted">Zarządzaj źródłami, z których agent pobiera wydarzenia</p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            onClick={handleRunAll}
            disabled={
              triggerRun.isPending ||
              !canRunWebsites ||
              !sources?.some((source) => source.enabled && source.type === "website")
            }
          >
            <Play className="h-4 w-4 mr-2" />
            Przeszukaj strony WWW
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Dodaj źródło
          </Button>
        </div>
      </div>

      <div className="rounded-md border p-4 text-sm space-y-2" role="status">
        <p className="font-medium">
          {worker.isLoading
            ? "Sprawdzanie wykonawcy Codex…"
            : worker.isError
              ? "Nie udało się sprawdzić wykonawcy Codex"
              : canRunWebsites
                ? "Wykonawca Codex jest gotowy"
                : !worker.data?.configured
                  ? "Wykonawca Codex nie jest skonfigurowany"
                  : "Wykonawca Codex jest niedostępny"}
        </p>
        <p className="text-muted-foreground">
          Strony WWW przeszukujemy w ramach abonamentu Codex. Zakres: Ostrołęka, najbliższe 30 dni.
          Źródła API JSON uruchamiaj osobno przyciskiem w wierszu.
        </p>
        <p className="text-muted-foreground">
          Harmonogram jest wyłączony. Wyszukiwanie uruchamiasz ręcznie; postęp znajdziesz w historii
          uruchomień.
        </p>
        {worker.isError && (
          <Button variant="outline" size="sm" onClick={() => worker.refetch()}>
            Sprawdź ponownie
          </Button>
        )}
      </div>

      {/* Unhealthy sources warning banner */}
      {(() => {
        const unhealthySources = sources?.filter((s) => s.consecutiveFailures >= 3) ?? [];
        if (unhealthySources.length === 0) return null;
        return (
          <div className="flex gap-3 rounded-md border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-sm">
                {unhealthySources.length === 1
                  ? "1 źródło wymaga uwagi"
                  : `${unhealthySources.length} źródła wymagają uwagi`}
              </p>
              <ul className="space-y-1">
                {unhealthySources.map((s) => (
                  <li key={s.id} className="text-sm">
                    <span className="font-medium">{s.name}</span>
                    {s.lastError && (
                      <span className="text-red-600 dark:text-red-400">
                        {" "}
                        — {s.lastError.slice(0, 100)}
                        {s.lastError.length > 100 ? "…" : ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })()}

      {isLoading ? (
        <p className="text-muted-foreground">Ładowanie...</p>
      ) : !sources?.length ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg">Brak źródeł</p>
          <p className="text-sm mt-1">Dodaj pierwsze źródło, aby rozpocząć</p>
        </div>
      ) : (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nazwa</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Harmonogram</TableHead>
                <TableHead>Ostatnie uruchomienie</TableHead>
                <TableHead className="w-[140px]">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => (
                <TableRow key={source.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn("h-2.5 w-2.5 rounded-full shrink-0", {
                          "bg-pulso-success": source.consecutiveFailures === 0,
                          "bg-pulso-warning":
                            source.consecutiveFailures > 0 && source.consecutiveFailures < 3,
                          "bg-pulso-orange": source.consecutiveFailures >= 3,
                        })}
                        title={
                          source.consecutiveFailures === 0
                            ? "Działa poprawnie"
                            : source.consecutiveFailures < 3
                              ? `${source.consecutiveFailures} kolejne błędy`
                              : `${source.consecutiveFailures} kolejnych błędów — wymaga uwagi`
                        }
                      />
                      {source.name}
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                        title={source.url}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={source.type === "json_api" ? "info" : "neutral"}>
                      {source.type === "json_api" ? "API" : "WWW"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={source.enabled ? "info" : "neutral"}>
                        {source.enabled ? "Aktywne" : "Wyłączone"}
                      </Badge>
                      {source.consecutiveFailures > 0 && (
                        <Badge variant="danger" className="whitespace-nowrap">
                          <AlertTriangle className="h-3 w-3" />
                          {source.consecutiveFailures} błędów
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="neutral"
                      title={
                        source.scheduleCron ? "Zapisane ustawienie nie jest wykonywane" : undefined
                      }
                    >
                      Ręcznie
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {source.lastRunAt
                      ? new Date(source.lastRunAt).toLocaleString("pl-PL")
                      : "Nigdy"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <ActionButton
                        tooltip="Uruchom"
                        onClick={() => handleRunSource(source.id)}
                        disabled={
                          triggerRun.isPending ||
                          !source.enabled ||
                          (source.type !== "json_api" && !canRunWebsites)
                        }
                      >
                        <Play className="h-4 w-4" />
                      </ActionButton>
                      <ActionButton
                        tooltip={
                          source.type === "json_api"
                            ? "Reguły"
                            : "Reguły dostępne obecnie dla API JSON"
                        }
                        disabled={source.type !== "json_api"}
                        onClick={() => setRulesSource(source)}
                      >
                        <Settings2 className="h-4 w-4" />
                      </ActionButton>
                      <ActionButton tooltip="Edytuj" onClick={() => openEdit(source)}>
                        <Pencil className="h-4 w-4" />
                      </ActionButton>
                      <ActionButton tooltip="Usuń" onClick={() => handleDelete(source.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ActionButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}

      <AiSourceRulesDialog
        sourceId={rulesSource?.id ?? null}
        sourceName={rulesSource?.name}
        open={rulesSource !== null}
        onOpenChange={(open) => !open && setRulesSource(null)}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSource ? "Edytuj źródło" : "Dodaj nowe źródło"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nazwa</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="np. kupbilecik.pl"
              />
            </div>
            <div>
              <Label>URL startowy</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-1">
              <Label>Typ źródła</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value as "website" | "json_api" }))
                }
              >
                <option value="website">Strona WWW</option>
                <option value="json_api">API JSON</option>
              </select>
            </div>
            <div>
              <Label>Instrukcje dla agenta (opcjonalne)</Label>
              <Textarea
                value={form.instructions}
                onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
                placeholder="np. Szukaj wydarzeń w kalendarzu na najbliższe 30 dni..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Napisz po polsku wskazówki nawigacyjne i filtrujące dla agenta AI
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.name || !form.url || createSource.isPending || updateSource.isPending}
            >
              {editingSource ? "Zapisz" : "Dodaj"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
